import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../utils/animationVariants';
import Testimonials from '../components/Testimonials';
import CTA from '../components/Cta';
import HowItWorks from '../components/HowItWorks';
import NFCCards from '../components/NFCCards';
import { useSEO } from '../hooks/useSEO';

const PersonalCard = () => {
  const { ref, controls } = useScrollAnimation(0.1);
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [activeFeature, setActiveFeature] = useState(0);
  const featuresRef = useRef(null);
  const { ref: featuresAnimRef, controls: featuresControls } = useScrollAnimation(0.1);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  useSEO({
    title: "Personal Digital Business Cards | AM CARDMATE",
    description: "Create your perfect personal digital business card. Customizable, shareable, and always up-to-date contact information.",
    image: "/assets/personalcards.jpg",
    keywords: "personal business cards, individual cards, personal branding, digital cards, custom design"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AM CARDMATE Personal Digital Business Card",
        "description": "Create stunning digital business cards in seconds with built-in analytics and NFC technology",
        "brand": { "@type": "Brand", "name": "AM CARDMATE" },
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "GBP",
          "lowPrice": "0",
          "highPrice": "99.99",
          "offerCount": "3"
        },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "1247" }
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const personalCardFeatures = [
    { icon: 'bolt', title: 'Create in Seconds', description: 'Build your complete digital profile in under 60 seconds with all your contact info, social links, and more.', gradient: 'from-blue-400 to-blue-600', delay: 0 },
    { icon: 'chart-line', title: 'Powerful Analytics', description: 'Track views, contact saves, QR code scans, shares, device types, and daily activity patterns - get complete insights into your networking impact.', gradient: 'from-purple-400 to-purple-600', delay: 0.1 },
    { icon: 'wifi', title: 'Built-in NFC Writer', description: 'Transform any NFC tag into your digital card directly from the app - no external tools needed.', gradient: 'from-green-400 to-green-600', delay: 0.2 },
    { icon: 'layer-group', title: 'All-in-One Platform', description: 'Everything you need in one place: card creation, analytics, collections, and NFC programming.', gradient: 'from-pink-400 to-pink-600', delay: 0.3 }
  ];
  
  const subscriptionPlans = [
    { title: 'Monthly', price: '£4.99', period: '/month', features: ['Create up to 20 unique cards', 'Comprehensive analytics dashboard', 'Unlimited card shares', 'Custom backgrounds & themes', 'Priority customer support', 'Cancel anytime'], popular: false, buttonText: 'Start Monthly', gradient: 'from-gray-400 to-gray-600', description: 'Perfect for trying out premium features' },
    { title: 'Annual', price: '£39.99', period: '/year', yearlyPrice: '£3.33/month', features: ['Everything in Monthly plan', 'Save 33% compared to monthly', 'Advanced analytics insights', 'Early access to new features', 'Annual billing convenience', 'Same-day support response'], popular: true, buttonText: 'Save with Annual', gradient: 'from-blue-400 to-purple-600', description: 'Best value for professionals', savings: '4 months FREE' },
    { title: 'Lifetime', price: '£99.99', period: 'one-time', features: ['Everything in Annual plan', 'FREE Premium NFC Card included', 'Lifetime access to all features', 'All future updates included', 'VIP support channel', 'No recurring fees ever'], popular: false, buttonText: 'Go Lifetime', gradient: 'from-purple-400 to-pink-600', description: 'One payment, lifetime access', bonus: 'Includes NFC Card' }
  ];

  return (
    <>
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-white via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="badge badge-blue inline-flex mb-4">
                <FontAwesomeIcon icon="id-card" />
                <span>Personal Cards</span>
              </div>
              <h1 className="text-h1 mb-4 md:mb-6">
                Your Complete <span className="text-gradient-blue-pink">Digital Identity</span> in One App
              </h1>
              <p className="text-body-lg text-gray mb-6 md:mb-8">
                Create stunning digital cards in seconds, track comprehensive analytics, and share instantly with built-in NFC technology. Everything you need, nothing you don't.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 mt-6">
                <button onClick={() => setShowComingSoonPopup(true)} className="btn btn-primary group w-full sm:w-auto text-btn-lg">
                  <FontAwesomeIcon icon="download" />
                  <span>Download the App</span>
                  <FontAwesomeIcon icon="arrow-right" className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                </button>
                <button onClick={() => setShowDemoPopup(true)} className="btn btn-outline group w-full sm:w-auto text-btn-lg">
                  <FontAwesomeIcon icon="play-circle" />
                  <span>Watch Demo</span>
                </button>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto">
              <div className="relative">
                <div className="w-full bg-white rounded-3xl shadow-xl overflow-hidden">
                  <div className="h-32 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  </div>
                  <div className="relative px-6 pb-6">
                    <div className="w-24 h-24 rounded-full border-4 border-white bg-gradient-to-br from-blue-400 to-purple-500 -mt-12 mb-4 flex items-center justify-center text-white text-3xl font-bold shadow-lg">JD</div>
                    <h3 className="text-h3 text-dark mb-1">John Doe</h3>
                    <p className="text-body text-gray mb-4">Creative Director</p>
                    <div className="flex gap-3 mb-6">
                      {['linkedin-in', 'twitter', 'instagram'].map((social, index) => (
                        <button key={index} className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray hover:bg-gradient-to-r hover:from-blue-400 hover:to-purple-500 hover:text-white transition-all duration-300">
                          <FontAwesomeIcon icon={['fab', social]} />
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[{ icon: 'phone', text: 'Call' }, { icon: 'envelope', text: 'Email' }, { icon: 'globe', text: 'Website' }].map((action, index) => (
                        <button key={index} className="py-3 px-4 bg-gray-50 rounded-xl hover:bg-gradient-to-r hover:from-blue-400 hover:to-purple-500 hover:text-white transition-all duration-300 group">
                          <FontAwesomeIcon icon={action.icon} className="mb-1" />
                          <div className="text-caption font-medium">{action.text}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-400 to-green-600 text-white rounded-2xl px-4 py-2 shadow-xl">
                  <FontAwesomeIcon icon="check-circle" className="mr-2" />
                  <span className="text-btn-sm">Live Preview</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-purple inline-flex mb-6">
                <FontAwesomeIcon icon="star" />
                <span>Why Go Digital</span>
              </div>
              <h2 className="text-h2 mb-6">
                The Smart Choice for <span className="text-gradient-blue-pink">Modern Professionals</span>
              </h2>
              <p className="text-body-lg text-gray-600 max-w-3xl mx-auto">
                Paper business cards are outdated. Join the digital revolution and see why professionals are making the switch.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {[{ stat: '88%', desc: 'of paper cards are thrown away within a week' }, { stat: '3.5x', desc: 'more memorable than traditional cards' }, { stat: '100%', desc: 'contact accuracy guaranteed' }, { stat: '£0', desc: 'cost to update your information' }].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-h1 text-gradient-blue-pink mb-2">{item.stat}</div>
                  <p className="text-body text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-gradient-to-b from-white to-gray-50">
        <HowItWorks />
      </div>

      <section className="py-20 md:py-32 bg-gradient-to-b from-gray-50 via-white to-white relative overflow-hidden">
        <div className="absolute top-[20%] right-[10%] w-[600px] h-[600px] bg-gradient-to-br from-green-200/20 via-blue-200/20 to-transparent rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="relative w-full">
                <div className="relative max-w-full lg:max-w-xl mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 to-purple-100/50 rounded-3xl transform rotate-3 scale-105"></div>
                  <img src="/assets/image7.jpg" alt="Professional networking success" className="relative z-10 w-full rounded-3xl shadow-2xl" style={{ minHeight: '400px', maxHeight: '600px', objectFit: 'cover' }} />
                  <div className="absolute -top-6 -right-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl px-4 sm:px-6 py-3 sm:py-4 shadow-xl z-20">
                    <div className="text-h3">Trusted</div>
                    <div className="text-body-sm">by Professionals</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="badge badge-green inline-flex mb-6">
                  <FontAwesomeIcon icon="chart-line" />
                  <span>Proven Results</span>
                </div>
                <h2 className="text-h2 mb-6">
                  Real Impact for <span className="text-gradient-blue-pink">Real Professionals</span>
                </h2>
                <p className="text-body-lg text-gray-600 mb-10">
                  Don't just take our word for it. See the measurable impact AM CARDMATE has on professional networking success.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                  {[{ metric: '87%', label: 'Higher follow-up rate', desc: 'compared to paper cards', icon: 'arrow-trend-up', color: 'from-blue-400 to-blue-600' }, { metric: '5x', label: 'More connections made', desc: 'at networking events', icon: 'users', color: 'from-purple-400 to-purple-600' }, { metric: '92%', label: 'Contact accuracy', desc: 'no more typos or lost info', icon: 'check-circle', color: 'from-green-400 to-green-600' }, { metric: '60sec', label: 'Average setup time', desc: 'from download to first share', icon: 'clock', color: 'from-pink-400 to-pink-600' }].map((item, index) => (
                    <div key={index} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
                      <div className="flex items-start gap-4">
                        <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-white shadow-lg flex-shrink-0`}>
                          <FontAwesomeIcon icon={item.icon} className="text-xl" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-h3 text-gray-900 mb-1">{item.metric}</div>
                          <h4 className="text-h5 text-gray-900 mb-1">{item.label}</h4>
                          <p className="text-body-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <a href="https://apps.apple.com/pt/app/am-cardmate/id6747951046" target="_blank" rel="noopener noreferrer" className="btn btn-primary group inline-flex">
                  <FontAwesomeIcon icon="rocket" />
                  <span>Get Started Now</span>
                  <FontAwesomeIcon icon="arrow-right" className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-pink inline-flex mb-4">
                <FontAwesomeIcon icon="sparkles" />
                <span>Features</span>
              </div>
              <h2 className="text-h2 mb-6">
                Everything You Need to <span className="text-gradient-blue-pink">Stand Out</span>
              </h2>
              <p className="text-body-lg text-gray-600 max-w-3xl mx-auto">
                The most complete digital card solution with built-in analytics and NFC technology. Perfect for professionals who want to make lasting impressions.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {personalCardFeatures.map((feature, index) => (
                <div key={index} className="relative group">
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <FontAwesomeIcon icon={feature.icon} className="text-white text-xl" />
                      </div>
                      <div>
                        <h3 className="text-h4 text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-body text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              {[{ icon: 'share-alt', text: 'Universal Sharing' }, { icon: 'qrcode', text: 'QR Code Built-in' }, { icon: 'lock', text: 'Privacy Controls' }, { icon: 'sync', text: 'Instant Updates' }, { icon: 'mobile-alt', text: 'Mobile Optimized' }, { icon: 'globe', text: 'Works Everywhere' }, { icon: 'save', text: 'Contact Collections' }, { icon: 'bell', text: 'Smart Notifications' }].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md transition-all">
                  <FontAwesomeIcon icon={item.icon} className="text-primary-blue" />
                  <span className="text-body-sm font-medium text-dark">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#6FB1FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#DE6161]/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 mb-8">
              <span className="text-white">Choose Your </span>
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">Perfect Plan</span>
            </h2>
            <button onClick={() => setShowComingSoonPopup(true)} className="btn inline-flex items-center gap-3 px-8 py-4 text-btn-lg bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg">
              <FontAwesomeIcon icon="download" />
              <span>Download the App</span>
            </button>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-purple-50/30" id="pricing-plans">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="tag" />
              <span>Pricing Plans</span>
            </div>
            <h2 className="text-h2 mb-4 md:mb-6">
              Simple, <span className="text-gradient-blue-pink">Transparent Pricing</span>
            </h2>
            <p className="text-body-lg text-gray mb-4 md:mb-8">
              Start free, upgrade when you're ready. No hidden fees, cancel anytime.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {subscriptionPlans.map((plan, index) => (
              <div key={index} className="relative">
                <div className={`bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 relative h-full ${plan.popular ? 'ring-2 ring-blue-400/50' : ''}`}>
                  {plan.popular && <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue-400 to-purple-500"></div>}
                  <div className="p-6 lg:p-8 text-center">
                    {plan.popular && <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-400 to-purple-500 text-white text-caption font-bold rounded-full mb-4"><FontAwesomeIcon icon="star" />MOST POPULAR</div>}
                    {plan.bonus && <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-400 to-pink-500 text-white text-caption font-bold rounded-full mb-4"><FontAwesomeIcon icon="gift" />{plan.bonus}</div>}
                    {plan.savings && <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 text-caption font-bold rounded-full mb-4"><FontAwesomeIcon icon="tag" />{plan.savings}</div>}
                    <h3 className="text-h3 text-dark mb-2">{plan.title}</h3>
                    <p className="text-body-sm text-gray mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center gap-1 mb-2">
                      <span className="text-h1 text-gradient-blue-pink">{plan.price}</span>
                      <span className="text-body text-gray">{plan.period}</span>
                    </div>
                    {plan.yearlyPrice && <p className="text-body-sm text-green-600 font-medium mb-4">Only {plan.yearlyPrice}</p>}
                  </div>
                  <div className="px-6 lg:px-8 pb-6 lg:pb-8">
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r ${index === 0 ? 'from-teal-400 to-teal-600' : index === 1 ? 'from-blue-400 to-purple-600' : 'from-purple-400 to-pink-600'} flex items-center justify-center mt-0.5`}>
                            <FontAwesomeIcon icon="check" className="text-white text-xs" />
                          </div>
                          <span className="text-body text-gray">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => setShowComingSoonPopup(true)} className={`btn w-full justify-center text-btn-lg ${plan.popular ? 'bg-gradient-to-r from-blue-400 to-purple-500 text-white hover:from-blue-500 hover:to-purple-600' : 'bg-gray-100 text-dark hover:bg-gradient-to-r hover:from-blue-400 hover:to-purple-500 hover:text-white'}`}>
                      {plan.buttonText}
                      <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-body text-gray mb-4">
              <FontAwesomeIcon icon="shield-alt" className="text-green-500 mr-2" />
              Secure • No setup fees • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50/30 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="badge badge-purple inline-flex mb-4">
                  <FontAwesomeIcon icon="wifi" />
                  <span>NFC Technology</span>
                </div>
                <h2 className="text-h2 mb-6">
                  Take It to the <span className="text-gradient-blue-pink">Next Level</span>
                </h2>
                <p className="text-body-lg text-gray-600 mb-8">
                  Upgrade to our premium NFC cards and share your profile with just a tap. Choose from eco-friendly bamboo, sleek black, or vibrant gradient designs.
                </p>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[{ icon: 'leaf', name: 'Eco Bamboo', color: 'from-amber-400 to-amber-600' }, { icon: 'star', name: 'Executive Black', color: 'bg-black' }, { icon: 'palette', name: 'Vibrant Gradient', color: 'from-blue-500 to-purple-600' }].map((card, index) => (
                    <div key={index} className="text-center">
                      <div className={`w-16 h-16 ${card.color.includes('bg-') ? card.color : `bg-gradient-to-br ${card.color}`} rounded-xl flex items-center justify-center text-white mx-auto mb-2`}>
                        <FontAwesomeIcon icon={card.icon} />
                      </div>
                      <p className="text-body-sm font-medium">{card.name}</p>
                    </div>
                  ))}
                </div>
                <a href="/nfc-cards" className="btn btn-primary group inline-flex">
                  <FontAwesomeIcon icon="shopping-cart" />
                  <span>Shop NFC Cards</span>
                  <FontAwesomeIcon icon="arrow-right" className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              </div>
              <div className="relative">
                <img src="/assets/image28.png" alt="NFC Cards Collection" className="w-full rounded-3xl shadow-2xl" />
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-dark rounded-2xl px-4 sm:px-6 py-2 sm:py-3 shadow-xl font-bold z-20">Starting at £9.99</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gradient-to-b from-white to-blue-50/30" id="app-download">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 text-blue-600 text-btn-sm mb-6">
              <FontAwesomeIcon icon="mobile-alt" />
              <span>Available Now</span>
            </div>
            <h2 className="text-h1 mb-6">
              Get <span className="text-gradient-blue-pink">AM CARDMATE</span> Today
            </h2>
            <p className="text-body-lg text-gray-600 max-w-2xl mx-auto mb-12">
              Join countless professionals already using AM CARDMATE. Available on all major platforms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a href="https://apps.apple.com/pt/app/am-cardmate/id6747951046" target="_blank" rel="noopener noreferrer" className="flex items-center bg-black text-white px-8 py-4 rounded-2xl hover:bg-gray-900 transition-all group">
                <FontAwesomeIcon icon={['fab', 'apple']} className="text-2xl mr-4" />
                <div className="text-left">
                  <div className="text-caption text-white opacity-90">Download on the</div>
<div className="text-btn-lg text-white">App Store</div>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.amcardmate.app" target="_blank" rel="noopener noreferrer" className="flex items-center bg-black text-white px-8 py-4 rounded-2xl hover:bg-gray-900 transition-all group">
                <FontAwesomeIcon icon={['fab', 'google-play']} className="text-2xl mr-4" />
                <div className="text-left">
                 <div className="text-caption text-white opacity-90">GET IT ON</div>
<div className="text-btn-lg text-white">Google Play</div>
                </div>
              </a>
            </div>
            <div className="flex items-center justify-center gap-8 text-body text-gray-600">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="star" className="text-yellow-500" />
                <span className="font-semibold">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="download" className="text-green-500" />
                <span className="font-semibold">Used by Companies Globally</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-gradient-to-b from-blue-50/30 to-white">
        <Testimonials customTitle="Loved by Professionals" customDescription="Join thousands who've transformed their networking with AM CARDMATE" customBadgeText="Success Stories" />
      </div>

      <CTA title="Ready to Transform Your Networking?" description="Download the app today. No credit card required." primaryButtonText="Download the App" primaryButtonIcon="download" primaryButtonHref="https://apps.apple.com/pt/app/am-cardmate/id6747951046" secondaryButtonText="View Pricing" secondaryButtonIcon="tag" secondaryButtonHref="#pricing-plans" background="gradient" />

      {showDemoPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDemoPopup(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-primary-blue to-accent-purple rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon="video" className="text-white text-2xl sm:text-3xl" />
              </div>
              <h3 className="text-h3 text-gray-900 mb-4">Demo Video Coming Soon!</h3>
              <p className="text-body text-gray-600 mb-8">We're creating an amazing demo video to show you all the features of AM CARDMATE. It'll be ready very soon!</p>
              <div className="space-y-3">
                <button className="w-full py-3 bg-gradient-to-r from-primary-blue to-accent-purple text-white rounded-xl text-btn" onClick={() => setShowDemoPopup(false)}>OK, Got It!</button>
                <button className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl text-btn hover:bg-gray-200 transition-colors" onClick={() => { setShowDemoPopup(false); document.getElementById('pricing-plans').scrollIntoView({ behavior: 'smooth' }); }}>View Pricing Instead</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showComingSoonPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowComingSoonPopup(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon="rocket" className="text-white text-2xl sm:text-3xl" />
              </div>
              <h3 className="text-h3 text-gray-900 mb-4">Download AM CARDMATE</h3>
              <p className="text-body text-gray-600 mb-8">AM CARDMATE is now available on the App Store and Google Play!</p>
              <div className="grid grid-cols-1 gap-3 mb-6">
                <button onClick={() => window.open('https://apps.apple.com/pt/app/am-cardmate/id6747951046', '_blank')} className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all">
                  <FontAwesomeIcon icon={['fab', 'apple']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">Download on the</div>
                    <div className="text-btn-lg">App Store</div>
                  </div>
                </button>
                <button onClick={() => window.open('https://play.google.com/store/apps/details?id=com.amcardmate.app', '_blank')} className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all">
                  <FontAwesomeIcon icon={['fab', 'google-play']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">GET IT ON</div>
                    <div className="text-btn-lg">Google Play</div>
                  </div>
                </button>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-btn hover:from-blue-600 hover:to-purple-700 transition-all" onClick={() => setShowComingSoonPopup(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PersonalCard;